import React, { Component, type ErrorInfo, type ReactNode } from "react";

import * as Sentry from "@sentry/browser";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";

import { PaymentError } from "@/lib/types/payment-error";

interface PaymentErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
  showFallback?: boolean;
  showToast?: boolean;
}

interface PaymentErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class PaymentErrorBoundary extends Component<
  PaymentErrorBoundaryProps,
  PaymentErrorBoundaryState
> {
  constructor(props: PaymentErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): PaymentErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      errorInfo
    });

    if (import.meta.env.NODE_ENV === "development") {
      console.error("PaymentErrorBoundary caught an error:", error, errorInfo);
    }

    // Capture comprehensive error information for Sentry
    Sentry.withScope((scope) => {
      scope.setTag("errorBoundary", "PaymentErrorBoundary");
      scope.setLevel("error");

      // Add error type and class name
      scope.setTag("errorType", error.constructor.name);
      scope.setTag("isPaymentError", error instanceof PaymentError);

      // Enhanced context with all available information
      const errorContext: Record<string, any> = {
        componentStack: errorInfo.componentStack,
        errorMessage: error.message,
        errorStack: error.stack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      // Extract PaymentError-specific properties
      if (error instanceof PaymentError) {
        errorContext.paymentError = {
          code: error.code,
          method: error.method,
          field: error.field,
          userMessageKey: error.userMessageKey,
          providerMessage: error.providerMessage,
          isRetryable: error.isRetryable()
        };

        // Add original error if present
        if (error.originalError) {
          errorContext.originalError = {
            message: error.originalError?.message,
            name: error.originalError?.constructor?.name,
            stack: error.originalError?.stack
          };
        }

        // Set error fingerprint for better grouping
        scope.setFingerprint([
          "PaymentErrorBoundary",
          error.code,
          error.method || "unknown"
        ]);

        // Add payment-specific tags
        scope.setTag("paymentErrorCode", error.code);
        if (error.method) scope.setTag("paymentMethod", error.method);
        if (error.field) scope.setTag("paymentField", error.field);
      } else {
        // For non-PaymentError, use message-based fingerprinting
        scope.setFingerprint([
          "PaymentErrorBoundary",
          "generic",
          error.message || "unknown"
        ]);
      }

      scope.setContext("errorDetails", errorContext);

      // Add breadcrumb for error boundary catch
      Sentry.addBreadcrumb({
        message: "Error caught by PaymentErrorBoundary",
        category: "error",
        level: "error",
        data: {
          errorType: error.constructor.name,
          isPaymentError: error instanceof PaymentError
        }
      });

      Sentry.captureException(error);
    });

    if (this.props.showToast !== false) {
      let errorMessage = "A payment error occurred. Please try again.";

      if (error instanceof PaymentError) {
        // Use PaymentError's user-friendly message if available
        errorMessage = error.message || errorMessage;
      } else {
        // For generic errors, use the message if it's user-friendly
        errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage, {
        duration: 5000,
        action: {
          label: "Retry",
          onClick: this.handleRetry
        }
      });
    }

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  render() {
    const { hasError, error } = this.state;
    const { children, fallback, showFallback = true } = this.props;

    if (hasError && showFallback) {
      if (fallback) {
        return fallback;
      }

      return (
        <Card className="mx-auto mt-40 max-w-md">
          <CardHeader className="text-center">
            <div className="bg-destructive/10 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <AlertTriangle className="text-destructive h-6 w-6" />
            </div>
            <CardTitle className="text-destructive">Payment Error</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted-foreground mb-1">
              {"Something went wrong during payment processing."}
            </p>
            <p className="text-muted-foreground text-xs">
              Please try again or contact support if the problem persists.
            </p>
            <div className="mt-8 flex gap-2">
              <Button onClick={this.handleRetry} size="sm" className="w-1/2">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.location.reload()}
                className="w-1/2"
              >
                Reload Page
              </Button>
            </div>
            {import.meta.env.NODE_ENV === "development" && error && (
              <details className="mt-4 text-left">
                <summary className="text-muted-foreground cursor-pointer text-xs">
                  Error Details (Development)
                </summary>
                <pre className="text-muted-foreground bg-muted mt-2 overflow-auto rounded p-2 text-xs">
                  {error.message}
                  {this.state.errorInfo && (
                    <>
                      {"\n\n"}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      );
    }

    return children;
  }
}

export function withPaymentErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Partial<PaymentErrorBoundaryProps>
) {
  const WrappedComponent = (props: P) => (
    <PaymentErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </PaymentErrorBoundary>
  );

  WrappedComponent.displayName = `withPaymentErrorBoundary(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
}
